import React, { useState, useMemo, useCallback } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  createReview,
  deleteReview,
  getReviewDetail,
  listReviews,
  updateReview,
} from "./callbacks";
import { ReviewFormValues } from "../../../types/api/review";
import { QData } from "../../../types/basic-type-list";

export const useReviewList = (qData?: QData) => {
  const [extraQuery, setExtraQuery] = useState<Record<string, any>>({});

  const mergedQData = useMemo(() => {
    return { ...qData, ...extraQuery };
  }, [qData, extraQuery]);

  const queryResult = useQuery({
    queryKey: ["/review", mergedQData],
    queryFn: async () => {
      const res = await listReviews(mergedQData);
      return res.data;
    },
  });

  const refetchWithQuery = useCallback((newQData: QData) => {
    setExtraQuery(newQData);
  }, []);

  return {
    ...queryResult,
    refetchWithQuery,
  };
};

export const useReviewCreate = ({
  onSuccess,
  onError,
}: {
  qData?: QData;
  onSuccess?: (res?: any) => void;
  onError?: (err?: any) => void;
} = {}) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: ReviewFormValues) => createReview(data),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ["/review"] });
      onSuccess?.(res);
    },
    onError: (err: any) => {
      onError?.(err);
    },
  });
};

export const useReviewUpdate = ({
  onSuccess,
  onError,
}: {
  qData?: QData;
  onSuccess?: (res?: any) => void;
  onError?: (err?: any) => void;
} = {}) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<ReviewFormValues> }) =>
      updateReview({ id, data }),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ["/review"] });
      onSuccess?.(res);
    },
    onError: (err: any) => {
      onError?.(err);
    },
  });
};

export const useReviewDelete = ({
  onSuccess,
  onError,
}: {
  qData?: QData;
  onSuccess?: (res?: any) => void;
  onError?: (err?: any) => void;
} = {}) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteReview(id),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ["/review"] });
      onSuccess?.(res);
    },
    onError: (err: any) => {
      onError?.(err);
    },
  });
};

export const useReviewDetail = (id: string, options = {}) => {
  return useQuery({
    queryKey: ["/review", id],
    queryFn: async () => {
      const res = await getReviewDetail(id);
      return res.data;
    },
    enabled: !!id,
    ...options,
  });
};
